import React from 'react';
import { useMutation } from '@apollo/client';
import { Button, Typography, Loading, Spacer } from '@guildeducationinc/recess';
import { GuildUser } from '@guildeducationinc/guild-auth';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { ExecutionResult } from 'graphql';
import { datadogRum } from '@datadog/browser-rum';
import { formatPhone } from '@src/utilities/format';
import {
  UpsertContactStrategyInput,
  ContactStrategyResponse,
  ContactType,
  ContactTypeName,
} from '@src/utilities/userProfileUtility';
import Divider from '@src/components/Shared/Divider/Divider';
import RouteLeavingGuard from '@src/components/Shared/RouteLeavingGuard/RouteLeavingGuard';
import TextInputField from '@src/components/Shared/FormInputFields/TextInputField/TextInputField';
import { UPSERT_CONTACT_UPS } from '@src/graphql/profile_edit/mutations/updateProfile';
import { Server } from '@src/components/GqlProvider/GqlProvider';
import { PHONE_REGEX, PUBLIC_URL } from '@src/constants';
import '../EditProfile.scss';
import { clickable } from '@src/analytics';

export const DUPLICATE_EMAIL_ERROR_MESSAGE =
  'duplicate key value violates unique constraint "index_email_constraint"';

export const EMAIL_EXISTS = 'An account with this email address already exists.';

interface FormValues {
  email: string;
  phone: string;
}

type Props = {
  formData?: GuildUser;
  setFormData: React.Dispatch<React.SetStateAction<any>>;
  setShowError: React.Dispatch<React.SetStateAction<any>>;
  setShowSuccess: React.Dispatch<React.SetStateAction<any>>;
};

const validationSchema = Yup.object().shape({
  email: Yup.string()
    .email('Please enter a valid email address.')
    .required('Please enter an email address.'),
  phone: Yup.string()
    .matches(PHONE_REGEX, { message: 'Please enter a valid phone number.' })
    .required('Please enter a phone number.'),
});

const ContactInformation: React.FC<Props> = ({
  formData,
  setFormData,
  setShowError,
  setShowSuccess,
}) => {
  const [upsertContactStrategy] = useMutation<ContactStrategyResponse, UpsertContactStrategyInput>(
    UPSERT_CONTACT_UPS,
    {
      context: {
        server: Server.UPSV2,
      },
    }
  );

  // Check phone number for +1 in phone
  let phone = formData?.phone;
  if (phone && /^\+[0-9]/.test(phone)) {
    phone = formatPhone(phone.replace(/^\+[0-9]/, ''));
  } else if (phone && !/^\+[0-9]/.test(phone)) {
    phone = formatPhone(phone);
  }

  const initialValues = {
    email: formData?.email ?? '',
    phone: phone ?? '',
  };

  const onSubmit = async (
    values: FormValues,
    setFieldError: (field: string, message: string) => void
  ) => {
    setFieldError('email', '');
    try {
      const emailChanged = values.email !== initialValues.email;
      const upsertResults = await upsertChangedValues(values);
      const someError = upsertResults.some((result) => {
        const { errors } = result?.data?.ups_upsertContactStrategy || {};
        return !!errors?.length;
      });
      if (someError) {
        setShowSuccess(false);
        setShowError(true);
      } else {
        // show success alert
        setFormData({ ...formData, ...values });
        setShowError(false);
        setShowSuccess(true);
        clickable({
          product: 'account',
          loggedIn: true,
          tag: 'account_profile',
          text: 'save_changes',
          type: 'button',
          location: 'contact_information',
        });
        if (emailChanged) {
          window.location.assign(PUBLIC_URL);
        }
      }
    } catch (error: any) {
      if (error.message.includes(DUPLICATE_EMAIL_ERROR_MESSAGE)) {
        setFieldError('email', EMAIL_EXISTS);
      }
      datadogRum.addError(error, {
        message: `There was an error when saving changes to contact information: ${error.message}`,
        stack: error.stack,
      });
      setShowSuccess(false);
      setShowError(true);
    }
  };

  const upsertChangedValues = async (values: FormValues): Promise<ExecutionResult[]> => {
    const upsertResults: ExecutionResult[] = [];
    for (const key in values) {
      if (values[key] !== initialValues[key]) {
        const upsertContact = {
          guildUuid: formData?.id as string,
          contactType:
            key === ContactTypeName.EMAIL.toLowerCase()
              ? ContactType.PRIMARY_EMAIL
              : ContactType.PRIMARY_PHONE,
          primary: true,
          value: values[key],
        };
        upsertResults.push(
          await upsertContactStrategy({
            variables: {
              upsertContact,
            },
          })
        );
      }
    }
    return upsertResults;
  };

  return (
    <Formik
      enableReinitialize={true}
      initialValues={initialValues}
      onSubmit={(values, { setFieldError }) => onSubmit(values, setFieldError)}
      validationSchema={validationSchema}
    >
      {({ dirty, handleBlur, handleChange, handleSubmit, isSubmitting, isValid, values }) => (
        <>
          <RouteLeavingGuard when={dirty} />
          <Loading
            label='edit profile'
            loaded={!isSubmitting}
            showChildrenWhileLoading={true}
            overlay={true}
          >
            <Typography variant='h3'>Contact information</Typography>
            <Typography variant='body1' className='padding--top_8 padding--bottom_32'>
              * Required
            </Typography>
            <Form>
              <div className='accountFormFields padding--bottom_24'>
                <Spacer pb={24}>
                  <TextInputField
                    label='Personal email'
                    required={true}
                    name='email'
                    placeholder='Personal email'
                    onBlur={handleBlur}
                    onChange={handleChange}
                    type='email'
                    value={values.email}
                    helpText='If you update your email, you&lsquo;ll have to sign back in to your account.'
                  />
                </Spacer>
                <Spacer pb={24}>
                  <TextInputField
                    label='Phone number'
                    required={true}
                    name='phone'
                    onBlur={handleBlur}
                    onChange={handleChange}
                    mask='phone'
                    type='tel'
                    value={values.phone}
                  />
                </Spacer>
              </div>
              <Divider color='black-lighten-87' />
              <div className='flex flexJustify--end padding--top_24'>
                <Button
                  type='submit'
                  onSubmit={() => handleSubmit()}
                  disabled={!dirty || !isValid || isSubmitting}
                  testid='account-save-button'
                >
                  Save changes
                </Button>
              </div>
            </Form>
          </Loading>
        </>
      )}
    </Formik>
  );
};

export default ContactInformation;
